import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Heading, LiveRegion, Text } from '../atoms';
import { Icon } from '../atoms/Icon';
import { Inline } from '../atoms/Inline';
import { Stack } from '../atoms/Stack';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { useTranslation } from '../../i18n/useTranslation';
import { useTimeline } from '../../hooks/useTimeline';
import { TimelineSkeleton } from './TimelineSkeleton';
import { TimelineZoomControls, type TimelineZoomRange } from './TimelineZoomControls';
import { timelineEventHref, timelineEventLabel } from './timelineEventText';
import type { TimelineEvent, TimelineEventType } from '../../types/timeline';
import './DashboardTimeline.css';

export interface DashboardTimelineProps {
  /** The signed-in user's own, currently selected aquarium. Never another user's. */
  aquariumId: string;
}

/** The card shows the newest events; the full list is on the timeline page. */
const MAX_EVENTS = 8;

type EventTone = 'neutral' | 'info' | 'success' | 'warning';

/** API event type → icon (text label always next to it, never colour alone). */
const ICON_FOR_TYPE: Record<TimelineEventType, { icon: string; tone: EventTone }> = {
  journal_entry: { icon: 'book', tone: 'neutral' },
  image: { icon: 'image', tone: 'neutral' },
  measurement: { icon: 'thermometer', tone: 'info' },
  icp_analysis: { icon: 'flask', tone: 'info' },
  dosing: { icon: 'droplet', tone: 'neutral' },
  livestock: { icon: 'fish', tone: 'success' },
  task: { icon: 'wrench', tone: 'neutral' },
  ai_insight: { icon: 'sparkles', tone: 'info' },
};

function iconFor(event: TimelineEvent): { icon: string; tone: EventTone } | undefined {
  if (event.type === 'livestock' && event.subtype === 'deceased') return { icon: 'fish', tone: 'warning' };
  return Object.prototype.hasOwnProperty.call(ICON_FOR_TYPE, event.type) ? ICON_FOR_TYPE[event.type] : undefined;
}

function formatDateLabel(isoDate: string, locale: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(parsed);
}

/**
 * The aquarium's latest events as a vertical list (icon, translated title, date),
 * one per row so it fits the dashboard's narrow side column. Every row links to a
 * page that exists (timelineEventHref).
 */
export function DashboardTimeline({ aquariumId }: DashboardTimelineProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [zoom, setZoom] = useState<TimelineZoomRange>('all');

  const { data, isLoading, isError, refetch } = useTimeline(aquariumId, undefined, zoom);

  const events = useMemo<TimelineEvent[]>(
    () => data?.pages.flatMap((page) => page.data.events) ?? [],
    [data],
  );
  const knownEvents = useMemo(() => events.filter((event) => iconFor(event) !== undefined), [events]);
  const hasUnknownEventTypes = knownEvents.length < events.length;
  const shown = knownEvents.slice(0, MAX_EVENTS);

  const header = (
    <Stack gap="3" className="dashboard-timeline__header">
      <Heading level={3}>{t('dashboard.timeline.title')}</Heading>
      <TimelineZoomControls range={zoom} onChange={setZoom} disabled={isLoading || isError} />
    </Stack>
  );

  let body;
  if (isLoading) {
    body = (
      <>
        <TimelineSkeleton />
        <LiveRegion as="span" visuallyHidden busy>
          {t('dashboard.timeline.loading')}
        </LiveRegion>
      </>
    );
  } else if (isError) {
    body = (
      <LoadErrorState
        compact
        title={t('dashboard.timeline.error.title')}
        titleLevel={4}
        message={t('dashboard.timeline.error.description')}
        retryLabel={t('dashboard.timeline.error.retry')}
        onRetry={() => void refetch()}
        live="polite"
      />
    );
  } else if (shown.length === 0) {
    body = (
      <Stack gap="3">
        <Stack gap="2">
          <Heading level={4}>
            {zoom === 'all' ? t('dashboard.timeline.empty.title') : t('dashboard.timeline.empty.rangeTitle')}
          </Heading>
          <Text color="secondary">{t('dashboard.timeline.empty.description')}</Text>
        </Stack>
        <Inline>
          <Button variant="primary" onClick={() => navigate(`/aquariums/${aquariumId}/journal`)}>
            {t('dashboard.timeline.empty.action')}
          </Button>
        </Inline>
      </Stack>
    );
  } else {
    body = (
      <Stack gap="3">
        {hasUnknownEventTypes && (
          <Text size="sm" color="secondary" className="dashboard-timeline__gap-notice">
            {t('dashboard.timeline.partial.gapNotice')}
          </Text>
        )}
        <ul className="dashboard-timeline__list" aria-label={t('dashboard.timeline.trackLabel')}>
          {shown.map((event) => {
            const style = iconFor(event)!;
            return (
              <li key={event.id} className="dashboard-timeline__item" data-testid="timeline-event">
                <Link
                  to={timelineEventHref(event, aquariumId)}
                  className="dashboard-timeline__link"
                  data-event-type={event.type}
                >
                  <span className={`dashboard-timeline__icon dashboard-timeline__icon--${style.tone}`} aria-hidden="true">
                    <Icon name={style.icon} size="sm" />
                  </span>
                  <span className="dashboard-timeline__text">
                    <span className="dashboard-timeline__title">{timelineEventLabel(event, t)}</span>
                    <time className="dashboard-timeline__date" dateTime={event.occurredAt}>
                      {formatDateLabel(event.occurredAt, i18n.language)}
                    </time>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        <Inline justify="end" className="dashboard-timeline__footer">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/aquariums/${aquariumId}/timeline`)}>
            {t('dashboard.timeline.viewFull')}
          </Button>
        </Inline>
      </Stack>
    );
  }

  return (
    <Card data-testid="dashboard-timeline" className="dashboard-timeline">
      <Stack gap="4">
        {header}
        {body}
      </Stack>
    </Card>
  );
}

export default DashboardTimeline;
